import i18n from "@/i18n";
import { AUTH_METHODS, RegisterUser } from "@/types";

export type ValidateReturn<T extends object> =
  | {
      isValid: true;
      data: T;
    }
  | {
      isValid: false;
      errors: ValidateError<T>;
    };

export type ValidateError<T> = {
  [key in keyof T]?: string;
};

// #region validateEmail
export function validateEmail(email: string): boolean {
  // validate email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
// #endregion validateEmail

// #region validateRegistration
export function validateRegistration(
  newUser: RegisterUser
): ValidateReturn<RegisterUser> {
  //	Full Name (Mandatory, Text, Max 100 characters)
  //	Email Address (Mandatory, Unique, Text, Max 150 characters)
  //	Password (Mandatory, Text, Min 8 characters, 1 special character, Max 50 characters)
  //	Phone Number (Optional, Numeric, 10-15 digits)
  //	Company Name (Optional, Text, Max 100 characters)
  //	Authentication Method (Dropdown - Google, Office 365, Facebook, LinkedIn)
  const errors: ValidateError<RegisterUser> = {};
  const trimmedFullName = newUser.fullName?.trim() || "";
  if (!trimmedFullName) {
    errors.fullName = i18n.t("Full name is required");
  } else if (trimmedFullName.length > 100) {
    errors.fullName = i18n.t("Full name max length 100 characters");
  }

  const trimmedEmail = newUser.email?.trim() || "";
  if (!trimmedEmail) {
    errors.email = i18n.t("Email is required");
  } else if (trimmedEmail.length > 150) {
    errors.email = i18n.t("Email max length 150 characters");
  } else if (!validateEmail(trimmedEmail)) {
    errors.email = i18n.t("Invalid email format");
  }

  if (!newUser.password) {
    errors.password = i18n.t("Password is required");
  } else if (newUser.password.length < 8) {
    errors.password = i18n.t("Password must be at least 8 characters");
  } else if (newUser.password.length > 50) {
    errors.password = i18n.t("Password max length 50 characters");
  } else if (!/[^A-Za-z0-9]/.test(newUser.password)) {
    errors.password = i18n.t(
      "Password must contain at least one special character"
    );
  }

  const trimmedPhoneNumber = newUser.phoneNumber?.trim() || undefined;
  if (
    trimmedPhoneNumber &&
    (trimmedPhoneNumber.length < 10 || trimmedPhoneNumber.length >= 15)
  ) {
    errors.phoneNumber = i18n.t("Phone number must be 10-15 digits");
  }
  if (
    trimmedPhoneNumber &&
    trimmedPhoneNumber &&
    !/^\d+$/.test(trimmedPhoneNumber)
  ) {
    errors.phoneNumber = i18n.t("Phone number must only contain numbers");
  }

  const trimmedCompanyName = newUser.companyName?.trim() || undefined;
  if (trimmedCompanyName && trimmedCompanyName.length > 100) {
    errors.companyName = i18n.t("Company name max length 100 characters");
  }

  if (!AUTH_METHODS.includes(newUser.authMethod)) {
    errors.authMethod = i18n.t("Invalid authentication method");
  }

  if (Object.keys(errors).length > 0) {
    console.error("Registration validation failed", errors);
    return { isValid: false, errors };
  }

  return {
    isValid: true,
    data: {
      fullName: trimmedFullName,
      email: trimmedEmail,
      password: newUser.password,
      phoneNumber: trimmedPhoneNumber,
      companyName: trimmedCompanyName,
      authMethod: newUser.authMethod,
    },
  };
}
// #endregion validateRegistration
